// Хелперы шага «Перевозка» (экспедирование). Данные приходят от внешнего приложения экспедитора
// (forwardingInfo), UI их только отображает.
import { isoToRu } from '@/lib/date';
import type { ForwardingInfo } from '@/types/deals';

/** ISO date / date-time → «dd.MM.yyyy» (берём первые 10 символов yyyy-mm-dd). */
export function fmtFwdDate(value: string | undefined): string | null {
  return value ? isoToRu(value.slice(0, 10)) : null;
}

/** Есть ли вообще данные экспедирования (иначе показываем баннер ожидания). */
export function hasForwardingData(info: ForwardingInfo | undefined): boolean {
  if (!info) {
    return false;
  }
  return Boolean(
    info.loadedDeliveryInfo?.loadingDays?.length ||
      info.ongoingDeliveryInfo?.vehicles?.length ||
      info.unloadedDeliveryInfo?.unloadedDays?.length ||
      info.plannedDeliveryInfo?.deliveryDays?.length,
  );
}

// Назначение экспедитора: идёт поиск/подбор экспедитора, пока он не ПРИМЕТ заявку (экспедитор ещё
// не назначен).
const AWAIT_ACCEPT_STEPS = new Set(['FORWARDING_PROCESSING', 'WAIT_FORWARDER_ACCEPT_ORDER', 'FORWARDER_ACCEPT_ORDER']);
// Поручение экспедитору: только активная подпись агента (черновик в его ЭДО). Стадия степпера —
// «Поручение экспедитора».
const INSTRUCTION_STEPS = new Set(['WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED']);
// Назначение перевозчика: заявка размещена на бирже перевозок и ждёт перевозчика (WAIT_FORWARDING_STARTING)
// либо он уже её взял (CARRIER_ASSIGNED). Отдельный шаг степпера перед «Перевозкой» (миграция V278).
const CARRIER_ASSIGN_STEPS = new Set(['WAIT_FORWARDING_STARTING', 'CARRIER_ASSIGNED']);
// Перевозка: машины вышли и далее до завершения доставки.
const TRANSPORT_STEPS = new Set([
  'FORWARDING_IN_PROGRESS', 'FORWARDER_REJECT_ORDER', 'FORWARDING_COMPLETED',
  'WAIT_DELIVERY', 'WAIT_WEIGHT_MODERATION', 'DELIVERY_COMPLETED',
]);

// Заголовок карточки шага форвардинга. Стадии степпера бэкенд теперь отдаёт корректно (миграция
// V274 разбила «Выбор грузополучателя» на выбор/назначение/поручение), а этот хелпер даёт заголовок
// панели по конкретному processing_step (или null — тогда берём info.stage — имя текущего шага).
export function forwardingStageTitleKey(processingStep: string): string | null {
  if (AWAIT_ACCEPT_STEPS.has(processingStep)) {
    return 'app.deals.forwarding.assignTitle';
  }
  if (INSTRUCTION_STEPS.has(processingStep)) {
    return 'app.deals.forwarding.instructionTitle';
  }
  if (CARRIER_ASSIGN_STEPS.has(processingStep)) {
    return 'app.deals.carrierAssign.title';
  }
  if (TRANSPORT_STEPS.has(processingStep)) {
    return 'app.deals.forwarding.transportTitle';
  }
  return null;
}

export type ForwardingPhase =
  | 'awaitAccept' // идёт поиск экспедитора — он ещё не принял заявку (шаг «Назначение экспедитора»)
  | 'rejected' // экспедитор отклонил
  | 'inProgress' // экспедитор приступил: распределение ТС → отгрузка/перевозка
  | 'waitDelivery' // ждём подтверждения получения покупателем
  | 'weightModeration' // модерация фактического веса
  | 'completed'; // перевозка завершена

export function forwardingPhase(processingStep: string): ForwardingPhase {
  if (processingStep === 'FORWARDER_REJECT_ORDER') {
    return 'rejected';
  }
  if (AWAIT_ACCEPT_STEPS.has(processingStep)) {
    return 'awaitAccept';
  }
  if (processingStep === 'WAIT_DELIVERY') {
    return 'waitDelivery';
  }
  if (processingStep === 'WAIT_WEIGHT_MODERATION') {
    return 'weightModeration';
  }
  if (processingStep === 'DELIVERY_COMPLETED') {
    return 'completed';
  }
  // FORWARDING_IN_PROGRESS / FORWARDING_COMPLETED — экспедитор приступил к перевозке.
  return 'inProgress';
}
